import { useEffect, useState } from 'react'

import { QuickFillButton } from './quick-fill-button'
import type { StocktakeLine } from '@/lib/api/stocktake-api'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'

import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { TableCell, TableRow } from '@/components/ui/table'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { cn } from '@/lib/utils'

const WARNING_THRESHOLD = 0.1

/**
 * Variance display: giữ dấu +/-, vi-VN locale (dấu chấm nghìn, dấu phẩy thập phân).
 * formatQty đã strip trailing zeros nên `-1` không hiện `-1.000`.
 */
function formatSigned(n: number) {
    return n > 0 ? `+${formatQty(n)}` : formatQty(n)
}

interface Props {
    line: StocktakeLine
    index: number
    editable: boolean
    onChange: (
        data: {
            counted_qty?: number | null
            line_notes?: string | null
        },
        workflow: ReturnType<typeof useWorkflowTracking>,
    ) => void
}

/**
 * StocktakeLineRow — 1 dòng bảng kiểm iPad-friendly.
 * Input touch-target ≥ 44px. Realtime variance khi user gõ.
 */
export function StocktakeLineRow({
    line,
    index,
    editable,
    onChange: submitChange,
}: Props) {
    const workflow = useWorkflowTracking('stocktake_count', {
        operation: 'update',
        active: editable,
        attemptKey: line.id,
        autoStart: false,
    })
    const onChange = (data: Parameters<Props['onChange']>[0]) => {
        workflow.interact('items')
        submitChange(data, workflow)
    }
    // Local state cho input số (chuỗi để cho phép rỗng khi đang gõ).
    const [countedStr, setCountedStr] = useState(
        line.counted_qty != null ? String(line.counted_qty) : '',
    )
    const [notesStr, setNotesStr] = useState(line.line_notes ?? '')

    useEffect(() => {
        setCountedStr(line.counted_qty != null ? String(line.counted_qty) : '')
    }, [line.counted_qty])

    // Compute variance realtime từ input.
    const parsedCounted =
        countedStr === '' ? null : Number.parseFloat(countedStr)
    const currentVariance =
        parsedCounted != null && !Number.isNaN(parsedCounted)
            ? parsedCounted - line.expected_qty
            : 0

    const isVariance = parsedCounted != null && currentVariance !== 0
    const varianceRatio =
        line.expected_qty > 0
            ? Math.abs(currentVariance) / line.expected_qty
            : 0
    const isWarning = isVariance && varianceRatio > WARNING_THRESHOLD

    // Backend UpdateLine SQL overwrites every field trên payload (COALESCE
    // không được dùng), nên FE phải gửi cả counted_qty và line_notes trong
    // mỗi mutation — nếu chỉ gửi 1 field, field còn lại sẽ bị wipe null.
    const buildPayload = (
        countedOverride?: number | null,
        notesOverride?: string,
    ) => {
        const notesRaw = notesOverride ?? notesStr
        const countedRaw = countedOverride === undefined ? countedStr : ''
        const counted =
            countedOverride !== undefined
                ? countedOverride
                : countedRaw === ''
                  ? null
                  : Number.parseFloat(countedRaw)
        return {
            counted_qty:
                counted === null || Number.isNaN(counted) ? null : counted,
            line_notes: notesRaw.trim() === '' ? null : notesRaw,
        }
    }

    const handleCountedBlur = () => {
        if (isWarning) workflow.step('warning_shown')
        const val = countedStr === '' ? null : Number.parseFloat(countedStr)
        if (val !== null && Number.isNaN(val)) return
        onChange(buildPayload())
    }

    const handleFillExpected = (value: number) => {
        setCountedStr(String(value))
        onChange(buildPayload(value))
    }

    const handleNotesBlur = () => {
        const val = countedStr === '' ? null : Number.parseFloat(countedStr)
        if (val !== null && Number.isNaN(val)) return
        onChange(buildPayload())
    }

    return (
        <TableRow className="h-14" data-lot-row-id={line.id}>
            <TableCell className="text-center text-muted-foreground">
                {index + 1}
            </TableCell>
            <TableCell className="font-mono text-sm">{line.lot_code}</TableCell>
            <TableCell className="text-sm">{line.warehouse_name}</TableCell>
            <TableCell className="text-sm">
                {line.material_or_product_name ?? '—'}
            </TableCell>
            <TableCell className="text-right tabular-nums">
                {formatQty(line.expected_qty)}
                {line.delta_after_snapshot !== 0 && (
                    <span
                        className={`ml-1 text-xs ${
                            line.delta_after_snapshot > 0
                                ? 'text-green-700'
                                : 'text-red-700'
                        }`}
                    >
                        ({formatSigned(line.delta_after_snapshot)})
                    </span>
                )}
            </TableCell>
            <TableCell>
                <div className="flex items-center gap-1">
                    <Input
                        type="number"
                        step="0.001"
                        min={0}
                        value={countedStr}
                        onChange={(e) => setCountedStr(e.target.value)}
                        onBlur={handleCountedBlur}
                        disabled={!editable}
                        className="h-11 min-w-24 text-right tabular-nums"
                    />
                    {editable && (
                        <QuickFillButton
                            expected={line.expected_qty}
                            onFill={handleFillExpected}
                        />
                    )}
                </div>
            </TableCell>
            <TableCell className="w-40 text-right tabular-nums">
                {isVariance ? (
                    <div className="flex items-center gap-1 justify-end">
                        <span
                            className={
                                currentVariance > 0
                                    ? 'text-green-700 font-semibold'
                                    : 'text-red-700 font-semibold'
                            }
                        >
                            {formatSigned(currentVariance)}
                        </span>
                        {isWarning && (
                            <Badge
                                variant="outline"
                                className="border-amber-400 text-amber-700 bg-amber-50"
                            >
                                {m.stocktake_variance_warning()}
                            </Badge>
                        )}
                    </div>
                ) : (
                    <span className="text-muted-foreground">—</span>
                )}
            </TableCell>
            <TableCell className="w-56">
                {/*
                 * Cột Ghi chú luôn hiển thị 1 input, luôn enable trừ khi
                 * session read-only. Chỉ đỏ viền khi variance lớn (isWarning)
                 * mà notes trống — signal user cần điền lý do trước khi
                 * submit.
                 */}
                <Input
                    value={notesStr}
                    onChange={(e) => setNotesStr(e.target.value)}
                    onBlur={handleNotesBlur}
                    disabled={!editable}
                    placeholder={m.stocktake_line_placeholder_notes()}
                    className={cn(
                        'h-11',
                        isWarning &&
                            notesStr.trim() === '' &&
                            'border-red-500 focus-visible:ring-red-500',
                    )}
                />
            </TableCell>
        </TableRow>
    )
}
